export type BeforeBack = () => void | Promise<void>;

export const runBeforeBack = async <T>(
  beforeBack: BeforeBack | undefined,
  next: () => T | Promise<T>
): Promise<T> => {
  await beforeBack?.();
  return next();
};

export const runHandledLayoutBack = async (
  leftFunc: (() => boolean | void | Promise<boolean | void>) | null | undefined,
  performDefaultBack: () => unknown
): Promise<boolean> => {
  const handled = await leftFunc?.();
  if (handled) return false;
  const navigationRequested = performDefaultBack();
  return navigationRequested !== false;
};

export type SingleFlightBeforeBackRunner = {
  run: (
    beforeBack: BeforeBack | undefined,
    next: () => boolean | Promise<boolean>
  ) => Promise<boolean>;
  reset: () => void;
};

export type BackRunnerEventTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;

export const bindBackRunnerToPopState = (
  runner: SingleFlightBeforeBackRunner,
  target: BackRunnerEventTarget
): (() => void) => {
  const reset = () => runner.reset();
  target.addEventListener('popstate', reset);
  return () => target.removeEventListener('popstate', reset);
};

export const createSingleFlightBeforeBackRunner = (): SingleFlightBeforeBackRunner => {
  let inFlight: Promise<boolean> | null = null;
  let lockedAfterLeave = false;
  let generation = 0;

  const reset = () => {
    generation += 1;
    inFlight = null;
    lockedAfterLeave = false;
  };

  const run: SingleFlightBeforeBackRunner['run'] = (beforeBack, next) => {
    if (inFlight) return inFlight;
    if (lockedAfterLeave) return Promise.resolve(true);

    const operationGeneration = generation;
    let didLeave = false;
    const operation = (async () => {
      try {
        await beforeBack?.();
        if (operationGeneration !== generation) return false;
        didLeave = await next();
        return didLeave;
      } finally {
        if (operationGeneration === generation) {
          inFlight = null;
          lockedAfterLeave = didLeave;
        }
      }
    })();
    inFlight = operation;
    return operation;
  };

  return { run, reset };
};
